import type { DriveItem } from '@/mocks/driveFiles';

// --- types from API ---
export interface ApiItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  mimeType: string | null;
  fileType: string;
  size: number;
  parentId: string | null;
  path: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiTrashItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  mimeType: string | null;
  size: number;
  deletedAt: string;
  originalPath: string;
  daysRemaining: number;
  originalName: string;
}

export interface DriveStats {
  totalStorage: number;
  usedStorage: number;
}

const API_BASE = '/api';

// --- Setup ---

export interface SetupStatus {
  configured: boolean;
  rootDir?: string;
}

export async function checkSetupStatus(): Promise<SetupStatus> {
  const res = await fetch(`${API_BASE}/setup/status`);
  if (!res.ok) {
    return { configured: true }; // assume configured if check fails
  }
  return res.json();
}

export async function configureSetup(rootDir: string): Promise<SetupStatus> {
  return request<SetupStatus>(`${API_BASE}/setup/configure`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rootDir }),
  });
}

async function request<T>(url: string, options?: any): Promise<T> {
  const res = await fetch(url, options);
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      message = body.message || body.error || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return res.json();
}

function mapItem(api: ApiItem): DriveItem {
  return {
    id: api.id,
    name: api.name,
    type: api.type,
    mimeType: api.mimeType ?? undefined,
    size: api.size,
    parentId: api.parentId,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
    path: api.path,
    fileType: api.fileType,
  };
}

// --- Files ---

export async function fetchAllFiles(): Promise<DriveItem[]> {
  const data = await request<{ items: ApiItem[] }>(`${API_BASE}/files/all`);
  return data.items.map(mapItem);
}

export async function fetchFiles(dirPath: string = ''): Promise<DriveItem[]> {
  const data = await request<{ items: ApiItem[]; path: string }>(
    `${API_BASE}/files?path=${encodeURIComponent(dirPath)}`
  );
  return data.items.map(mapItem);
}

export interface UploadItem {
  name: string;
  relativePath: string;
  file: File;
}

export async function uploadFiles(parentPath: string, fileList: File[]): Promise<DriveItem[]> {
  const formData = new FormData();
  fileList.forEach((f) => formData.append('files', f));

  const res = await fetch(
    `${API_BASE}/files/upload?path=${encodeURIComponent(parentPath)}`,
    { method: 'POST', body: formData }
  );
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const b = await res.json(); msg = b.message || msg; } catch { /* */ }
    throw new Error(msg);
  }
  const data = await res.json();
  return (data.uploaded as ApiItem[]).map(mapItem);
}

export async function uploadFolder(parentPath: string, items: UploadItem[]): Promise<DriveItem[]> {
  const formData = new FormData();
  items.forEach((item) => {
    formData.append('files', item.file);
    formData.append('paths', item.relativePath);
  });

  const res = await fetch(
    `${API_BASE}/files/upload-folder?path=${encodeURIComponent(parentPath)}`,
    { method: 'POST', body: formData }
  );
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const b = await res.json(); msg = b.message || msg; } catch { /* */ }
    throw new Error(msg);
  }
  const data = await res.json();
  return (data.uploaded as ApiItem[]).map(mapItem);
}

export async function uploadFilesWithProgress(
  parentPath: string,
  files: File[],
  onProgress: (percent: number, loaded: number) => void
): Promise<DriveItem[]> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/files/upload?path=${encodeURIComponent(parentPath)}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100), e.loaded);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const data = JSON.parse(xhr.responseText);
        resolve((data.uploaded as ApiItem[]).map(mapItem));
      } else {
        let msg = `HTTP ${xhr.status}`;
        try { const b = JSON.parse(xhr.responseText); msg = b.message || msg; } catch { /* */ }
        reject(new Error(msg));
      }
    };

    xhr.onerror = () => reject(new Error('네트워크 오류'));
    xhr.ontimeout = () => reject(new Error('업로드 시간 초과'));
    xhr.send(formData);
  });
}

export async function uploadFolderWithProgress(
  parentPath: string,
  items: UploadItem[],
  onProgress: (percent: number, loaded: number) => void
): Promise<DriveItem[]> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    items.forEach((item) => {
      formData.append('files', item.file);
      formData.append('paths', item.relativePath);
    });

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/files/upload-folder?path=${encodeURIComponent(parentPath)}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100), e.loaded);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const data = JSON.parse(xhr.responseText);
        resolve((data.uploaded as ApiItem[]).map(mapItem));
      } else {
        let msg = `HTTP ${xhr.status}`;
        try { const b = JSON.parse(xhr.responseText); msg = b.message || msg; } catch { /* */ }
        reject(new Error(msg));
      }
    };

    xhr.onerror = () => reject(new Error('네트워크 오류'));
    xhr.ontimeout = () => reject(new Error('업로드 시간 초과'));
    xhr.send(formData);
  });
}

export function getDownloadUrl(filePath: string): string {
  return `${API_BASE}/files/download?path=${encodeURIComponent(filePath)}`;
}

export function getPreviewUrl(filePath: string): string {
  return `${API_BASE}/files/preview?path=${encodeURIComponent(filePath)}`;
}

export function getOfficePreviewUrl(filePath: string): string {
  return `${API_BASE}/files/preview-office?path=${encodeURIComponent(filePath)}`;
}

export async function createFolder(parentPath: string, name: string): Promise<DriveItem> {
  const data = await request<{ item: ApiItem }>(
    `${API_BASE}/files/folder?path=${encodeURIComponent(parentPath)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }
  );
  return mapItem(data.item);
}

export async function moveItem(sourcePath: string, targetParentPath: string): Promise<void> {
  await request(`${API_BASE}/files/move`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ source: sourcePath, target: targetParentPath }),
  });
}

export async function renameItem(itemPath: string, newName: string): Promise<void> {
  await request(`${API_BASE}/files/rename`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path: itemPath, name: newName }),
  });
}

export async function deleteItem(itemPath: string): Promise<void> {
  await request(
    `${API_BASE}/files?path=${encodeURIComponent(itemPath)}`,
    { method: 'DELETE' }
  );
}

// --- Trash ---

export async function fetchTrash(): Promise<DriveItem[]> {
  const data = await request<{ items: ApiTrashItem[] }>(`${API_BASE}/trash`);
  return (data.items || []).map((t: ApiTrashItem) => ({
    id: t.id,
    name: t.originalName || t.name,
    type: t.type,
    mimeType: t.mimeType ?? undefined,
    size: t.size,
    parentId: null,
    createdAt: t.deletedAt,
    updatedAt: t.deletedAt,
    deletedAt: t.deletedAt,
    path: t.originalPath,
  }));
}

export async function restoreTrashItem(trashName: string): Promise<void> {
  await request(`${API_BASE}/trash/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: trashName }),
  });
}

export async function permanentlyDeleteTrashItem(trashName: string): Promise<void> {
  await request(
    `${API_BASE}/trash/permanent?id=${encodeURIComponent(trashName)}`,
    { method: 'DELETE' }
  );
}

export async function emptyTrash(): Promise<void> {
  await request(`${API_BASE}/trash/empty`, { method: 'DELETE' });
}

export async function restoreAllTrash(): Promise<void> {
  await request(`${API_BASE}/trash/restore-all`, { method: 'POST' });
}

// --- Stats ---

export async function fetchStats(): Promise<DriveStats> {
  return request<DriveStats>(`${API_BASE}/storage`);
}